// ExportOptionsDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  RadioGroup,
  Radio,
  Divider,
  TextField,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

export default function ExportOptionsDialog({ open, onClose, onExport, currentFilters }) {
  const [format, setFormat] = useState('excel');
  const [includeOutbound, setIncludeOutbound] = useState(currentFilters.includeOutbound);
  const [includeNonValidated, setIncludeNonValidated] = useState(
    currentFilters.includeNonValidated,
  );
  const [regressionTag, setRegressionTag] = useState('');

  // Sync modal state with control panel filters when dialog opens
  useEffect(() => {
    if (open) {
      setIncludeOutbound(currentFilters.includeOutbound);
      setIncludeNonValidated(currentFilters.includeNonValidated);
      setRegressionTag('');
    }
  }, [open, currentFilters.includeOutbound, currentFilters.includeNonValidated]);

  const handleExport = () => {
    onExport({
      format,
      includeOutbound,
      includeNonValidated,
      regressionTag,
    });
    onClose();
  };

  const handleCancel = () => {
    // Reset to current filter states
    setIncludeOutbound(currentFilters.includeOutbound);
    setIncludeNonValidated(currentFilters.includeNonValidated);
    setFormat('excel');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DownloadIcon />
          Export Options
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Regression Tag
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g., 25.103CORE"
            value={regressionTag}
            onChange={(e) => setRegressionTag(e.target.value)}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Export Format
          </Typography>
          <RadioGroup value={format} onChange={(e) => setFormat(e.target.value)}>
            <FormControlLabel
              value="excel"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChartIcon fontSize="small" sx={{ color: 'success.dark' }} />
                  <Typography>Excel (.xlsx)</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="csv"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography>CSV</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="pdf"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PictureAsPdfIcon fontSize="small" sx={{ color: 'error.main' }} />
                  <Typography>PDF</Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Include in Export
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={includeNonValidated}
                onChange={(e) => setIncludeNonValidated(e.target.checked)}
                sx={{
                  '&.Mui-checked': {
                    color: 'secondary.main',
                  },
                }}
              />
            }
            label="Non-Validated Events (Extra events not in flow)"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={includeOutbound}
                onChange={(e) => setIncludeOutbound(e.target.checked)}
                sx={{
                  '&.Mui-checked': {
                    color: 'secondary.main',
                  },
                }}
              />
            }
            label="Outbound Scenarios (Kochava, Epsilon, Mixpanel, etc.)"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={<DownloadIcon />}
          sx={{
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.light',
            },
          }}
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
}
